import type { Size } from "odyssey-shared";
import { readSetting, writeSetting, type SettingsStore } from "../settings.ts";
import { POPOUT_SIZE_KEY, formatPopoutSize, parsePopoutSize } from "./popout.ts";

/**
 * The pop-out's window controller (P2.12-T18, ADR-100's amendment of 2026-10-09, decisions 30 and 34, and of 2026-10-14,
 * decision 50): it opens the window the map is drawn into, Document Picture-in-Picture where the browser has it and a plain
 * same-origin window else, both from the click, at the size last kept on the device (320 by 320 when none), and ends it
 * the same way however it ends: Pop in, the window's own close and the page's `pagehide` all close it, keep its size and
 * tell the listeners. It holds the window and nothing of the map; the page's store and pictures are drawn into it by a
 * portal (`popout-view.tsx`), so no socket, request or subscription is opened for it.
 */

/**
 * The part of a browser window the controller uses.
 */
export interface PopoutFrame {
  readonly document: Document;
  readonly innerWidth: number;
  readonly innerHeight: number;
  readonly devicePixelRatio: number;
  close(): void;
  focus(): void;
  addEventListener(type: "pagehide" | "resize", listener: () => void): void;
  removeEventListener(type: "pagehide" | "resize", listener: () => void): void;
}

/**
 * What the browser offers, as the controller asks for it.
 */
export interface PopoutEnvironment {
  readonly picture: { requestWindow(size: Size): Promise<PopoutFrame> } | null;
  readonly plain: ((size: Size) => PopoutFrame | null) | null;
  onPageHide(listener: () => void): () => void;
  styles(into: Document): void;
}

/**
 * How an attempt to open ended: the window is open, a blocker refused a plain window, or the browser has neither way.
 */
export type PopoutOutcome = "opened" | "blocked" | "unsupported";

/**
 * Which way the open window was made.
 */
export type PopoutKind = "picture" | "plain";

/**
 * The window's name, so a second plain window is never opened beside the first.
 */
const PLAIN_NAME = "odyssey-map-popout";

/**
 * The controller of the one pop-out window.
 */
export class MapPopout {
  private frame: PopoutFrame | null = null;
  private way: PopoutKind | null = null;
  private pending: Promise<PopoutOutcome> | null = null;
  private arrive: (() => void) | null = null;
  private last: Size = { width: 0, height: 0 };
  private stopPageHide: (() => void) | null = null;
  private readonly watchers = new Set<() => void>();
  private readonly closers = new Set<() => void>();

  /**
   * @param environment What the browser offers.
   * @param storage Where the size is kept, or null for none.
   */
  public constructor(
    private readonly environment: PopoutEnvironment,
    private readonly storage: SettingsStore | null,
  ) {}

  /**
   * Whether either way of opening exists, which is whether the control is drawn.
   *
   * @returns True when the browser has Picture-in-Picture or a window to open.
   */
  public available(): boolean {
    return this.environment.picture !== null || this.environment.plain !== null;
  }

  /**
   * The open window, for `useSyncExternalStore`.
   *
   * @returns The window, or null when none is open.
   */
  public getSnapshot = (): PopoutFrame | null => this.frame;

  /**
   * Hears the window open and close.
   *
   * @param listener Called on each change.
   * @returns A function that stops listening.
   */
  public subscribe = (listener: () => void): (() => void) => {
    this.watchers.add(listener);
    return () => {
      this.watchers.delete(listener);
    };
  };

  /**
   * Which way the open window was made.
   *
   * @returns The way, or null when none is open.
   */
  public kind(): PopoutKind | null {
    return this.way;
  }

  /**
   * Hears the window end, by Pop in, by the player closing it or by the page leaving.
   *
   * @param listener Called once each time an open window ends.
   * @returns A function that stops listening.
   */
  public onClosed(listener: () => void): () => void {
    this.closers.add(listener);
    return () => {
      this.closers.delete(listener);
    };
  }

  /**
   * Opens the window. The browser is asked in the caller's own turn, so a click is the gesture it needs. With a window
   * already open it is brought forward and nothing more is opened; while a Picture-in-Picture request is unanswered a
   * second ask shares its answer.
   *
   * @param arrive Called when a window has opened, before anyone watching is told, so the map is shown as popped out
   *   before the view looks for a window that nothing yet says it should hold.
   * @returns "opened"; "blocked" when a plain window was refused; "unsupported" when the browser has neither way.
   */
  public open(arrive: (() => void) | null = null): Promise<PopoutOutcome> {
    if (this.frame !== null) {
      this.frame.focus();
      return Promise.resolve("opened");
    }
    if (this.pending !== null) {
      return this.pending;
    }
    this.arrive = arrive;
    const size = readSetting(POPOUT_SIZE_KEY, parsePopoutSize, this.storage);
    const picture = this.environment.picture;
    if (picture !== null) {
      let asked: Promise<PopoutFrame>;
      try {
        asked = picture.requestWindow(size);
      } catch {
        asked = Promise.reject(new Error("refused"));
      }
      const pending = asked.then(
        (frame) => this.take(frame, "picture"),
        () => this.plainWindow(size),
      );
      this.pending = pending;
      const clear = (): void => {
        this.pending = null;
      };
      void pending.then(clear, clear);
      return pending;
    }
    return Promise.resolve(this.plainWindow(size));
  }

  /**
   * Opens a plain window.
   *
   * @param size The size to ask for.
   * @returns The outcome.
   */
  private plainWindow(size: Size): PopoutOutcome {
    const plain = this.environment.plain;
    if (plain === null) {
      return "unsupported";
    }
    let frame: PopoutFrame | null;
    try {
      frame = plain(size);
    } catch {
      frame = null;
    }
    return frame === null ? "blocked" : this.take(frame, "plain");
  }

  /**
   * Takes a window that opened: its styles are copied once, it is watched for a resize and its close, and the page's
   * own leaving is watched.
   *
   * @param frame The window.
   * @param way Which way it was made.
   * @returns "opened".
   */
  private take(frame: PopoutFrame, way: PopoutKind): PopoutOutcome {
    this.environment.styles(frame.document);
    this.frame = frame;
    this.way = way;
    this.last = { width: frame.innerWidth, height: frame.innerHeight };
    frame.addEventListener("resize", this.onResize);
    frame.addEventListener("pagehide", this.onGone);
    this.stopPageHide = this.environment.onPageHide(() => this.close());
    const arrive = this.arrive;
    this.arrive = null;
    arrive?.();
    this.tell(this.watchers);
    return "opened";
  }

  /**
   * Keeps the window's size as it changes, so the size kept is the last one it had.
   */
  private readonly onResize = (): void => {
    if (this.frame !== null) {
      this.last = { width: this.frame.innerWidth, height: this.frame.innerHeight };
    }
  };

  /**
   * Ends the window the player closed, which is already going.
   */
  private readonly onGone = (): void => {
    this.end(false);
  };

  /**
   * Closes the window, if one is open: Pop in, and the page's logout or leaving.
   */
  public close(): void {
    this.end(true);
  }

  /**
   * Brings the open window forward, which a browser may ignore. It does nothing with none open.
   */
  public focus(): void {
    this.frame?.focus();
  }

  /**
   * Ends the open window: its size is kept, its listeners removed, the window closed when it is not already going, and
   * the watchers and closers told.
   *
   * @param shut Whether the window is still to be closed.
   */
  private end(shut: boolean): void {
    const frame = this.frame;
    if (frame === null) {
      return;
    }
    this.frame = null;
    this.way = null;
    frame.removeEventListener("resize", this.onResize);
    frame.removeEventListener("pagehide", this.onGone);
    this.stopPageHide?.();
    this.stopPageHide = null;
    if (this.storage !== null) {
      writeSetting(POPOUT_SIZE_KEY, formatPopoutSize(this.last), this.storage);
    }
    if (shut) {
      frame.close();
    }
    this.tell(this.watchers);
    this.tell(this.closers);
  }

  /**
   * Calls each of a set of listeners.
   *
   * @param listeners The listeners.
   */
  private tell(listeners: ReadonlySet<() => void>): void {
    for (const listener of [...listeners]) {
      listener();
    }
  }
}

/**
 * Copies the page's style sheets into a new document once: each sheet's rules as a style element (which carries the font
 * faces too), or a link for a sheet whose rules a browser will not read.
 *
 * @param from The page's document.
 * @param into The pop-out's document.
 */
export function copyStyles(from: Document, into: Document): void {
  for (const sheet of Array.from(from.styleSheets)) {
    let text: string | null;
    try {
      text = Array.from(sheet.cssRules)
        .map((rule) => rule.cssText)
        .join("\n");
    } catch {
      text = null;
    }
    if (text !== null) {
      const style = into.createElement("style");
      style.textContent = text;
      into.head.append(style);
    } else if (sheet.href !== null) {
      const link = into.createElement("link");
      link.rel = "stylesheet";
      link.href = sheet.href;
      into.head.append(link);
    }
  }
}

/**
 * The browser's own offer for this page: Document Picture-in-Picture where the page is the top one and the browser has
 * it, and a plain same-origin popup of a blank page where `window.open` exists. A page in a frame gets neither, so
 * the control is hidden there and a framed game gets the float (ADR-100 decision 50).
 *
 * @param page The page's window.
 * @returns The environment.
 */
export function browserPopout(page: Window): PopoutEnvironment {
  const api = (page as unknown as { readonly documentPictureInPicture?: { requestWindow(options: { readonly width: number; readonly height: number }): Promise<unknown> } }).documentPictureInPicture;
  let top = false;
  try {
    top = page.top === page;
  } catch {
    top = false;
  }
  return {
    picture: api === undefined || !top ? null : { requestWindow: (size) => api.requestWindow({ width: size.width, height: size.height }) as Promise<PopoutFrame> },
    plain:
      typeof page.open !== "function" || !top
        ? null
        : (size) => {
            const opened = page.open("", PLAIN_NAME, `popup=yes,width=${size.width},height=${size.height}`);
            return opened === null ? null : (opened as unknown as PopoutFrame);
          },
    onPageHide: (listener) => {
      page.addEventListener("pagehide", listener);
      return () => page.removeEventListener("pagehide", listener);
    },
    styles: (into) => copyStyles(page.document, into),
  };
}
